import React from 'react';
import { EvangileItem } from '../../hooks/useEvangile';
import { buildPassageUrl } from '../../utils/url-builder';
import ListenButton from './ListenButton';

interface CardMiniatureProps {
  evangile: EvangileItem;
  isToday: boolean;
  onClick: () => void;
  dateLabel: string;
  readLabel: string;
  listenLabel: string;
  pauseLabel: string;
}

const CardMiniature: React.FC<CardMiniatureProps> = ({
  evangile,
  isToday,
  onClick,
  dateLabel,
  readLabel,
  listenLabel,
  pauseLabel
}) => {
  const lang = window.location.pathname.split('/')[1] || 'fr';
  const passageUrl = buildPassageUrl(evangile.reference, lang);
  const detailUrl = `/${lang}/evangile/${evangile.date}`;

  return (
    <div
      className={`relative w-full text-left bg-white rounded-xl overflow-hidden border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
        isToday ? 'border-amber-600 shadow-[0_2px_10px_rgba(217,119,6,0.2)]' : 'border-amber-200 shadow-sm'
      }`}
      role="group"
    >
      {/* Stretched link: couvre toute la carte, visible pour les crawlers, sans imbriquer d'autres <a> */}
      <a
        href={detailUrl}
        onClick={e => { e.preventDefault(); onClick(); }}
        className="absolute inset-0 z-[1]"
        aria-label={evangile.accroche}
      />

      <div className="flex">
        <div className="relative w-[88px] min-w-[88px]">
          <img
            src={evangile.thumbUrl || evangile.imageUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="flex flex-1 flex-col gap-1 px-3 py-2 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[0.72rem] font-bold uppercase tracking-wider text-amber-600">
              {dateLabel}
            </span>
            <span className="flex items-center gap-1">
              {/* Écouter le passage à côté de la référence qui le fait lire —
                  sans ouvrir la fiche. */}
              {evangile.lectureAudioUrl && (
                <ListenButton
                  url={evangile.lectureAudioUrl}
                  listenLabel={listenLabel}
                  pauseLabel={pauseLabel}
                  variant="icon"
                />
              )}
              {/* z-[2] pour passer au-dessus du stretched link */}
              <span className="relative z-[2] text-[0.68rem] font-bold text-amber-900 bg-amber-100 border border-amber-200 rounded-full px-2 py-[2px] whitespace-nowrap">
                <a
                  href={passageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline"
                >
                  {evangile.reference}
                </a>
              </span>
            </span>
          </div>

          <p
            className="text-[0.95rem] text-amber-950 font-serif italic leading-snug"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {evangile.accroche}
          </p>

          <div className="flex items-center justify-between gap-2 text-amber-600 text-[0.75rem] font-semibold">
            <span className="flex items-center gap-1">
              <span>{readLabel}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </span>
            <span className="flex items-center gap-1 text-amber-500" title={`${evangile.views}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              {evangile.views}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardMiniature;
